import { Box as MuiBox, Grid } from '@mui/material'
import CellHOC from '@platform/react/hoc/list/cell'
import { debounce, PlatformEvent } from 'lib/util'
import { startTransition, useCallback, useEffect, useState } from 'react'
import Input from 'ui/Element/Field/Text'
import Button from 'ui/Element/Button'
import useCart from 'platform/react/hook/useCart'
import Icon from 'ui/Element/Icon'
import Amount from 'ui/Element/Field/Amount'
import Text from 'ui/Element/Text'
import OfflineHOC from 'platform/react/hoc/offline'
import { useMemoRef } from 'platform/react/hook'
import { makeBreakable } from 'lib/util/string'

const styles = theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
  },
  left: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
  },
  right: {
    display: 'flex',
    gap: '1.25rem',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    alignSelf: 'stretch',
  },
  infoAndComment: {
    display: 'flex',
    width: 'calc(100% - 6.25rem)', // 100% - width icon box - padding
    flexWrap: 'nowrap',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
  },
  commentButton: {
    padding: 0,
    height: 'auto!important',
    minHeight: '0!important',
  },
  icon: {
    display: 'flex',
    width: '5.25rem',
    height: '5.25rem',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.gray[960],
    borderRadius: '0.5rem',
  },
})

const Box = useMemoRef(OfflineHOC(({ disabled, onClick, forwardedRef, ...props }) => (
  <MuiBox
    {...props}
    ref={forwardedRef}
    onClick={disabled ? null : onClick}
    style={{
      opacity: disabled ? 0.25 : 1,
    }}
  />
)), false)

/**
 * Displays information about a part of the cart for the positions list.
 *
 * @param {Object} classes  styling for the cell
 * @param {Object} props    props for the cell
 *
 * @returns {*}
 */
const ListItemCellCartPosition = (classes, props = {}) => {
  const {
    id,
    name,
    partIcon,
    articleNumber,
    commentAddLabel,
    commentIcon,
    events,
  } = props

  const [showInput, setShowInput] = useState(false)
  const [inputDisabled, setInputDisabled] = useState(false)
  const { position } = useCart({ id, name })
  const { comment: initialComment = null, amount } = position || {}

  const [comment, setComment] = useState(initialComment)

  useEffect(() => {
    initialComment && setComment(initialComment)
  }, [initialComment])

  const handleAmount = useCallback((e) => {
    const clickEvent = new PlatformEvent(e, { key: id, name, amount: e.detail.value })
    events?.onAmount(clickEvent)
  }, [events, id, name, amount])

  const handleCommentClick = useCallback(() => setShowInput(!showInput), [])

  const handleCommentAdd = useCallback((e) => {
    setComment(e.detail.value)
    const clickEvent = new PlatformEvent('change', { key: id, name, comment: e.detail.value })
    startTransition(() => events?.onCommentAdd(clickEvent))

    if (e?.key === 'Enter') {
      setShowInput(false)
      setInputDisabled(false)
    }
  }, [])

  // const handleCommentAdd = useCallback((e) => {
  //   console.log('e', e.detail)
  //   setShowInput(false)
  //   setInputDisabled(false)
  //   setComment(e.detail.value)
  //
  //   const clickEvent = new PlatformEvent(e, { key: id, name, comment: e.detail.value })
  //   console.log('clickEvent', clickEvent)
  //   events?.onCommentAdd(clickEvent)
  // }, [events, id, name])

  const handleRemove = useCallback((e) => {
    const clickEvent = new PlatformEvent(e, { key: id, name })
    events?.onRemove?.(clickEvent)
  }, [events, id, name])

  return (
    <Grid
      container
      className={classes.container}
    >
      <Grid
        container
        item
        xs={12}
        className={classes.left}
      >
        <Box className={classes.icon}>
          <Icon
            source={partIcon}
            color={'gray.500'}
            size={'1.5rem'}
          />
        </Box>
        <Grid
          container
          className={classes.infoAndComment}
          sx={{ gap: comment || showInput ? '1.5rem' : undefined }}
        >
          <Grid
            container
            className={classes.info}
          >
            <Text
              align={'left'}
              maxLines={1}
              variant={'16/bold'}
              color={'black.main'}
              value={makeBreakable(name)}
            />
            <Text
              maxLines={1}
              variant={'14/medium'}
              color={'black.main'}
              value={articleNumber || '-'}
            />
          </Grid>
          <Grid container>
            {!comment || showInput ? (
              <>
                {showInput && (
                  <Box sx={{ width: '100%', marginTop: '0!important' }}>
                    <Input
                      multiline={true}
                      maxRows={3}
                      label={commentAddLabel}
                      value={comment || ''}
                      disabled={inputDisabled}
                      helperText={false}
                      events={{ onBlur: handleCommentAdd,
                        onKeyPress: (e) => {
                          if (e?.key === 'Enter') {
                            setInputDisabled(true)
                          }
                          handleCommentAdd(e)
                        } }}
                    />
                  </Box>
                )}
                {!showInput && (
                  <Button
                    bold={false}
                    variant={'text'}
                    fullWidth={false}
                    events={{ onClick: handleCommentClick }}
                    startIcon={{
                      name: commentIcon,
                      color: 'text.secondary',
                    }}
                    className={classes.commentButton}
                    value={commentAddLabel}
                    disabledWhenOffline
                    hideOfflineIcon
                  />
                )}
              </>
            ) : (
              <Grid
                item
                component={Box}
                container
                onClick={() => setShowInput(true)}
                disabledWhenOffline
                hideOfflineIcon
              >
                <Text
                  maxLines={3}
                  variant={'14/medium'}
                  color={'textDescription'}
                  value={comment}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={'auto'}
        className={classes.right}
      >
        <Box
          onClick={handleRemove}
          disabledWhenOffline
          hideOfflineIcon
        >
          <Icon
            name={'close'}
            color={'black'}
          />
        </Box>
        <Amount
          value={amount}
          events={{ onChange: handleAmount }}
          color={'black'}
          debounced={false}
          disabledWhenOffline
          disabledWhilePersisting={false}
        />
      </Grid>
    </Grid>
  )
}

export default CellHOC(ListItemCellCartPosition, styles)
